import React, { useEffect, useState } from 'react';
import { Box, Switch, Alert, Chip } from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { superAdminAPI } from '../../services/api';
import { SuperAdminDataTable, apiErrorMessage } from '../../components/superadmin/SuperAdminDataTable';
import { FIELD_TYPE_CATEGORIES } from '../../constants/fieldTypes';
import { brandColors, brandCss } from '../../theme/appTheme';

export const FieldTypesPage: React.FC = () => {
  const [types, setTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await superAdminAPI.getFieldTypes();
      setTypes(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load field types'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key: string) => {
    const target = types.find((t) => t.key === key);
    if (target?.system) return;
    const next = types.map((t) => (t.key === key ? { ...t, enabled: !t.enabled } : t));
    setTypes(next);
    try {
      await superAdminAPI.updateFieldTypes({ fieldTypes: next });
      setMessage('Field type updated');
    } catch (err) {
      setError(apiErrorMessage(err, 'Update failed'));
      load();
    }
  };

  return (
    <Box>
      <PageHeader
        title="Field Types"
        subtitle={getPageSubtitle('field_types')}
        icon={<TextFieldsIcon />}

      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      <SuperAdminDataTable
        rows={types}
        loading={loading}
        error={error}
        exportFileName="super-admin-field-types"
        exportSheetName="Field Types"
        recordLabel="types"
        searchPlaceholder="Search field types..."
        statusField="enabled"
        getStatus={(t) => (t.enabled ? 'active' : 'inactive')}
        statusOptions={[
          { label: 'All', value: 'all' },
          { label: 'Enabled', value: 'active' },
          { label: 'Disabled', value: 'inactive' },
        ]}
        columns={[
          {
            id: 'label',
            header: 'Type',
            searchValue: (t) => `${t.label} ${t.key} ${t.category}`,
            exportValue: (t) => t.label,
            render: (t) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <strong>{t.label}</strong>
                {t.system && (
                  <Chip label="System" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#e0e7ff', color: '#4338ca' }} />
                )}
              </Box>
            ),
          },
          {
            id: 'key',
            header: 'Key',
            searchValue: (t) => t.key,
            exportValue: (t) => t.key,
            render: (t) => <code style={{ fontSize: 12 }}>{t.key}</code>,
          },
          {
            id: 'category',
            header: 'Category',
            searchValue: (t) => t.category,
            exportValue: (t) => t.category,
            render: (t) => (
              <Chip
                label={FIELD_TYPE_CATEGORIES[t.category as keyof typeof FIELD_TYPE_CATEGORIES] || t.category}
                size="small"
                sx={{ height: 22, fontSize: 11, bgcolor: brandCss.green100, color: brandCss.primaryDark }}
              />
            ),
          },
          {
            id: 'db',
            header: 'DB Mapping',
            searchValue: (t) => t.dbMapping,
            exportValue: (t) => t.dbMapping,
            render: (t) => <span style={{ fontSize: 12, color: brandColors.textMuted }}>{t.dbMapping}</span>,
          },
          {
            id: 'ui',
            header: 'UI',
            searchValue: (t) => t.uiComponent,
            exportValue: (t) => t.uiComponent,
            render: (t) => <span style={{ fontSize: 12 }}>{t.uiComponent}</span>,
          },
          {
            id: 'desc',
            header: 'Description',
            searchValue: (t) => t.description,
            exportValue: (t) => t.description,
            render: (t) => <span style={{ color: brandColors.textMuted, fontSize: 12 }}>{t.description}</span>,
          },
        ]}
        renderActions={(t) => (
          <Switch
            checked={!!t.enabled}
            onChange={() => toggle(String(t.key))}
            color="success"
            size="small"
            disabled={!!t.system}
          />
        )}
      />
    </Box>
  );
};
